import React, { useState } from "react";
import { Table, Container } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEye, faTimes, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { useAppContext } from "../../../../context/appContext";

import { isViewOnly } from "../../../../utils/permissions";

import { MeasurementDetailsPanel } from "./MeasurementDetailsPanel";
import { DeleteSiteMeasurementModal, EditSiteMeasurementModal } from "../UpdateSiteMeasurement";

const formatDate = (dateString) => {
  if (!dateString) return "—";

  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
      // timeZoneName: 'short'
    }).format(date);
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString; // fallback to original if parsing fails
  }
};

const SiteMeasurementPage = ({
  env,
  handleEnvChange,
  currentMeasurements,
  currentTenant,
  currentTenantName,
  currentGroupVersion,
  ogmpCategories,
  versionsCategories
}) => {
  const { user } = useAppContext();
  const viewOnly = isViewOnly(user);

  const [selectedMeasurement, setSelectedMeasurement] = useState(null);
  const [showEditModal, setShowEditModal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expandedMeasurements, setExpandedMeasurements] = useState({});

  const handleEditClick = (currentMeasurement) => {
    setSelectedMeasurement(currentMeasurement);
    setShowEditModal(true);
  };
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedMeasurement(null);
  };

  const handleDeleteClick = (currentMeasurement) => {
    setSelectedMeasurement(currentMeasurement);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedMeasurement(null);
  };

  const toggleExpand = (measurementId) => {
    setExpandedMeasurements((prev) => ({
      ...prev,
      [measurementId]: !prev[measurementId]
    }));
  };

  return (
    <Container fluid>
      <Table striped bordered hover responsive className="table-sm">
        <thead>
          <tr>
            <th scope="col">Site Name</th>
            <th scope="col">Emissions</th>
            <th scope="col">Unit</th>
            <th scope="col">Date & Time</th>
            <th scope="col">Technology</th>
            <th scope="col">Provider</th>
            <th scope="col">Status</th>
            <th scope="col">UUID</th>
            <th scope="col" style={{ width: "80px" }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {currentMeasurements?.map((currentMeasurement, index) => {
            // Create a guaranteed unique ID using either the measurement's ID if it exists,
            // or fallback to the project ID with an index to ensure uniqueness
            const measurementId = currentMeasurement.id || `${currentMeasurement.project.id}-${index}`;

            return (
              <React.Fragment key={measurementId}>
                <tr
                  onClick={() => toggleExpand(measurementId)}
                  style={{ cursor: "pointer" }}
                  className={expandedMeasurements[measurementId] ? "table-active" : ""}
                >
                  <td>
                    <div className="d-flex justify-content-between align-items-center w-100">
                      <span>{currentMeasurement.project.name}</span>
                      <FontAwesomeIcon icon={expandedMeasurements[measurementId] ? faChevronUp : faChevronDown} size="sm" />
                    </div>
                  </td>
                  <td>{currentMeasurement.emissions}</td>
                  <td>{currentMeasurement.emissionsUnit}</td>
                  <td>{formatDate(currentMeasurement.measurementTimestamp)}</td>
                  <td>{currentMeasurement.measurementTechnology?.name}</td>
                  <td>{currentMeasurement.measurementProvider}</td>
                  <td>{currentMeasurement.status}</td>
                  <td>{currentMeasurement.id || currentMeasurement.project.id}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex justify-content-around align-items-center" style={{ minWidth: "80px" }}>
                      {viewOnly ? (
                        <FontAwesomeIcon icon={faEye} style={{ cursor: "pointer" }} onClick={() => handleEditClick(currentMeasurement)} />
                      ) : (
                        <>
                          <FontAwesomeIcon
                            icon={faEdit}
                            style={{ cursor: "pointer", marginRight: "10px" }}
                            onClick={() => handleEditClick(currentMeasurement)}
                          />
                          <FontAwesomeIcon
                            icon={faTimes}
                            style={{ cursor: "pointer", color: "red" }}
                            onClick={() => handleDeleteClick(currentMeasurement)}
                          />
                        </>
                      )}
                    </div>
                  </td>
                </tr>

                <MeasurementDetailsPanel
                  currentMeasurement={currentMeasurement}
                  measurementId={measurementId}
                  isExpanded={expandedMeasurements[measurementId]}
                />
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>

      {selectedMeasurement && (
        <>
          <EditSiteMeasurementModal
            show={showEditModal}
            viewOnly={viewOnly}
            siteMeasurement={selectedMeasurement}
            handleClose={handleCloseEditModal}
            handleEnvChange={handleEnvChange}
            currentTenant={currentTenant}
            currentTenantName={currentTenantName}
            currentGroupVersion={currentGroupVersion}
            env={env}
            ogmpCategories={ogmpCategories}
            versionsCategories={versionsCategories}
          />
          <DeleteSiteMeasurementModal
            show={showDeleteModal}
            siteMeasurement={selectedMeasurement}
            handleClose={handleCloseDeleteModal}
            handleEnvChange={handleEnvChange}
            currentTenant={currentTenant}
            currentTenantName={currentTenantName}
            currentGroupVersion={currentGroupVersion}
            env={env}
          />
        </>
      )}
    </Container>
  );
};

export { SiteMeasurementPage };
